/**
 * IsMobilecheck - A custom React hook to determine if the current device is mobile.
 *
 * This hook uses the window.matchMedia API to check if the viewport width is 
 * less than or equal to 991 pixels, which is a common breakpoint for mobile devices.
 * 
 * It initializes the state with the current match status and updates it 
 * whenever the viewport size changes.
 *
 * @returns {boolean} - Returns true if the device is mobile (viewport width <= 991px), 
 *                      otherwise returns false.
 *
 * Usage:
 * const isMobile = IsMobilecheck();
 * 
 * This hook can be used in functional components to conditionally render 
 * mobile-specific UI or apply different styles based on the device type.
 */
import React, { useState, useEffect } from 'react';

const IsMobilecheck = () => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(max-width: 991px)').matches;
    }
    return false; 
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 991px)');
    const updateIsMobile = () => {
      setIsMobile(mediaQuery.matches);
    };
    mediaQuery.addEventListener('change', updateIsMobile);
    return () => mediaQuery.removeEventListener('change', updateIsMobile);
  }, []);
  
  return isMobile;
};

export default IsMobilecheck;
